<template>
    <div>
        <div class="mb-4 flex items-center justify-between gap-2">
            <div class="flex min-w-0 flex-1 items-center gap-2">
                <UInput v-model="search" type="text" placeholder="Search..." icon="i-ph-magnifying-glass-duotone" class="w-full max-w-72" />
                <USelectMenu
                    v-model="statusFilter"
                    trailing
                    :class="['shrink-0', statusFilter ? 'w-52' : 'w-40']"
                    :leading-icon="statusFilter ? undefined : 'i-ph-warning-circle-duotone'"
                    :ui="{
                        icon: { base: '!size-4', trailing: { pointer: 'pointer-events-auto', base: '!size-4' } }
                    }"
                    :options="statusOptions"
                >
                    <template #label>
                        <template v-if="statusFilter">
                            <span class="text-gray-400">Status:</span>
                            <span class="truncate font-medium">{{ statusFilter.name }}</span>
                        </template>
                        <span v-else class="text-gray-400">Status</span>
                    </template>
                    <template #option="{ option }">
                        <span class="truncate">{{ option.name }}</span>
                    </template>
                    <template #trailing v-if="statusFilter">
                        <button @click.stop="statusFilter = undefined" type="button" class="flex items-center justify-center">
                            <UIcon name="i-ph-x-bold" class="size-3" />
                        </button>
                    </template>
                </USelectMenu>
            </div>
            <div class="flex shrink-0 items-center gap-2">
                <UButton color="white" icon="i-ph-clock-counter-clockwise-bold" label="Movements" @click="openMovements()" />
                <UButton v-if="canWrite" color="black" icon="i-ph-plus-bold" label="Restock" @click="openAction('restock')" />
            </div>
        </div>

        <UTable
            :rows="rows"
            :columns="columns"
            :loading="loading"
            :loading-state="{ icon: 'i-ph-circle-notch-bold', label: '' }"
            :empty-state="{ icon: 'i-ph-package-duotone', label: 'No stock.' }"
            :ui="{ td: { padding: 'py-3' }, tr: { base: 'cursor-pointer hover:bg-gray-50' } }"
            @select="(lot: StockLot) => openMovements(lot)"
        >
            <template #item-data="{ row }">
                <div class="min-w-[10rem] whitespace-normal leading-5">
                    <p>{{ row.item?.name }}</p>
                    <span class="text-sm text-gray-500">
                        <template v-if="row.item?.specification">{{ row.item.specification }} · </template>
                        <template v-if="row.lot_number">Lot <span class="font-mono">{{ row.lot_number }}</span> · </template>
                        {{ row.storage?.name }}<template v-if="isArchived(row)"> (Archived)</template>
                    </span>
                </div>
            </template>
            <template #qty-data="{ row }">
                <div class="text-right leading-5">
                    <p>
                        <span class="font-mono">{{ formatQty(row.qty_on_hand) }}</span>
                        <span class="ml-1 text-sm text-gray-500">{{ row.item?.unit }}</span>
                    </p>
                    <span v-if="row.expires_on" class="text-sm text-gray-500">Exp. {{ dayjs(row.expires_on).format('MMM YYYY') }}</span>
                    <span v-else class="text-sm text-gray-300">No expiry</span>
                </div>
            </template>
            <template #status-data="{ row }">
                <BadgeLotStatus :status="statusOf(row)" />
            </template>
            <template #actions-data="{ row }">
                <div @click.stop>
                    <UDropdown :items="lotMenu(row)">
                        <UButton color="gray" variant="ghost" icon="i-heroicons-ellipsis-horizontal-20-solid" />
                    </UDropdown>
                </div>
            </template>
        </UTable>

        <OverlayFieldPostStockAction v-model:open="overlays.action" :post="post" :mode="actionMode" :lot="selectedLot"
            :items="items" :destinations="destinations" @reload="fetchData" />
        <OverlayFieldPostMovements v-model:open="overlays.movements" :post="post" :lot="selectedLot" />
    </div>
</template>

<script setup lang="ts">
import dayjs from 'dayjs'
import { LOT_STATUS_META, type FieldPost, type InventoryItem, type StockActionMode, type StockLot } from '~/types/fieldPost'
import { formatQty, lotStatus, todayIso, type LotStatus } from '~/utils/fieldPosts'

const props = defineProps<{
    post: FieldPost
    canWrite: boolean
}>()

const client = useSupabaseClient()

const loading = ref(true)
const search = ref('')
const statusFilter = ref<{ value: 'alerts' | LotStatus; name: string }>()
const lots = ref<StockLot[]>([])
const items = ref<InventoryItem[]>([])
const destinations = ref<{ id: string; label: string }[]>([])
const selectedLot = ref<StockLot>()
const actionMode = ref<StockActionMode>('restock')
const overlays = reactive({ action: false, movements: false })
const today = todayIso()

const statusOptions = [
    { value: 'alerts', name: 'All Alerts' },
    ...(['negative', 'expired', 'expiring', 'ok'] as const).map(value => ({ value, name: LOT_STATUS_META[value].label })),
]

const columns = [
    { key: 'item', label: 'Item' },
    { key: 'qty', label: 'Quantity', class: 'text-right' },
    { key: 'status', label: 'Status', class: 'w-px' },
    { key: 'actions', class: 'w-px' },
]

function statusOf(lot: StockLot): LotStatus {
    return lotStatus(lot, today)
}

const storageOrder = computed(() => new Map((props.post.storages ?? []).map((s, i) => [s.id, i])))

function isArchived(lot: StockLot) {
    return !storageOrder.value.has(lot.storage_id)
}

function matchesStatus(lot: StockLot) {
    const filter = statusFilter.value?.value
    if (!filter) return true
    const status = statusOf(lot)
    return filter === 'alerts' ? status !== 'ok' : status === filter
}

const rows = computed(() => {
    const q = search.value.trim().toLowerCase()
    const order = (lot: StockLot) => storageOrder.value.get(lot.storage_id) ?? Number.MAX_SAFE_INTEGER
    return lots.value
        .filter(l => matchesStatus(l)
            && (!q || `${l.item?.name ?? ''} ${l.item?.specification ?? ''} ${l.lot_number ?? ''}`.toLowerCase().includes(q)))
        .sort((a, b) => order(a) - order(b)
            || (a.item?.name ?? '').localeCompare(b.item?.name ?? '')
            || (a.expires_on ?? '9999').localeCompare(b.expires_on ?? '9999'))
})

function lotMenu(lot: StockLot) {
    const history = [{ label: 'View Movements', icon: 'i-ph-clock-counter-clockwise-fill', click: () => openMovements(lot) }]
    if (!props.canWrite) return [history]
    return [
        [
            { label: 'Restock', icon: 'i-ph-plus-fill', click: () => openAction('restock', lot) },
            { label: 'Adjust Count', icon: 'i-ph-equals-fill', click: () => openAction('adjust', lot) },
            { label: 'Transfer', icon: 'i-ph-arrows-left-right-fill', click: () => openAction('transfer', lot) },
        ],
        [
            { label: 'Return to HDF', icon: 'i-ph-arrow-u-up-left-fill', iconClass: 'text-red-400', click: () => openAction('return_hdf', lot) },
            { label: 'Mark Expired', icon: 'i-ph-calendar-x-fill', iconClass: 'text-red-400', click: () => openAction('expired', lot) },
        ],
        history,
    ]
}

function openAction(mode: StockActionMode, lot?: StockLot) {
    actionMode.value = mode
    selectedLot.value = lot
    overlays.action = true
}

function openMovements(lot?: StockLot) {
    selectedLot.value = lot
    overlays.movements = true
}

onMounted(() => fetchData())

async function fetchData() {
    loading.value = true
    const [lotsRes, itemsRes, storagesRes] = await Promise.all([
        client.from('stock_lots').select('*, item:inventory_items (*), storage:field_post_storages (id, name)').eq('post_id', props.post.id),
        client.from('inventory_items').select('*').eq('is_archived', false).order('name'),
        client.from('field_post_storages').select('id, name, post:field_posts (name)').eq('is_archived', false).order('sort_order'),
    ])
    lots.value = (lotsRes.data ?? []) as StockLot[]
    items.value = (itemsRes.data ?? []) as InventoryItem[]
    destinations.value = ((storagesRes.data ?? []) as { id: string; name: string; post: { name: string } | null }[])
        .map(s => ({ id: s.id, label: `${s.post?.name ?? ''} · ${s.name}` }))
    loading.value = false
}
</script>
